import { OrderRuleStatus } from "@/features/line-sheet-sets/hooks/use-update-order-rule-status";
import {
  Agency,
  Boutique,
  Brand,
  BrandsType,
  ProductPrice,
  QuantityWithOption,
  StoredObject,
} from "@/features/types";
import {
  ConditionType,
  LightOrderSheet,
  LightOrderSheetSet,
  OrderSheetSet,
} from "@/features/order-sheets/order-sheet.type";
import { SheetProduct } from "@/features/order-sheets/sheet.type";

export interface LineSheetProduct {
  id: number;
  sku: string | null;
  modelNumber: string | null;
  name: string | null;
  brandId: number | null;
  brandName: string | null;
  categories: string[];
  gender: string | null;
  colorCode: string | null;
  colorName: string | null;
  material: string | null;
  season: string | null;
  supplyPrice: ProductPrice | null; //stock => retailPrice, season => costPrice
  costPrice: ProductPrice | null; //<<
  retailPrice: ProductPrice | null; //<<
  imageUrls: string[];
  alternativeImageUrls: string[];
  isConditionFixed: boolean | null;
  note: { [key: string]: string } | null;
  stockQuantityWithOptionList: QuantityWithOption[];
  priceAdjustedBy: PriceAdjustedBy;
  searchWord: string | null;
  isNew: boolean | null;
  aiImageChecked?: boolean | null;
  lineSheetProductSkuId?: number | null;
  // 백엔드가 SKU 연결을 의도적으로 skip한 경우 true (brand.sku_disabled OR lss.sku_disabled).
  isSkuSkipped?: boolean;
}

export function isSheetProduct(
  product: SheetProduct | LineSheetProduct
): product is SheetProduct {
  return (product as SheetProduct).orderQuantityWithOptionList !== undefined;
}

export interface PriceAdjustedBy {
  by: ConditionType;
  value: number;
  applyIva?: boolean | null;
  ivaValue?: string | null; // IVA 계산식(예: /1.22, *0.93). 미설정 시 기본 /1.22
}

export function isPriceAdjustedBy(obj: any): obj is PriceAdjustedBy {
  const priceAdjustedBy = obj as PriceAdjustedBy;
  return (
    priceAdjustedBy !== undefined &&
    priceAdjustedBy !== null &&
    (priceAdjustedBy.by === "MARKUP" || priceAdjustedBy.by === "DISCOUNT") &&
    priceAdjustedBy.value !== undefined &&
    typeof priceAdjustedBy.value === "number"
  );
}

export function isPriceAdjustedByType(str: any): str is PriceAdjustedBy["by"] {
  return str === "MARKUP" || str === "DISCOUNT";
}

export type LineSheetSetStatus =
  | "PARSING_PENDING"
  | "PARSING_COMPLETED"
  | "PARSING_FAILED"
  | "PARSING_SKIPPED";

export const LineSheetSetStatuses: LineSheetSetStatus[] = [
  "PARSING_PENDING",
  "PARSING_COMPLETED",
  "PARSING_FAILED",
  "PARSING_SKIPPED",
];

export function isLineSheetSetStatus(
  value: string
): value is LineSheetSetStatus {
  return LineSheetSetStatuses.some((t) => t === value);
}

export type LineSheetStatus =
  | "PARSING_SKIPPED"
  | "PARSING_PENDING"
  | "PARSING_FAILED"
  | "PARSED";

export const LineSheetStatuses: LineSheetStatus[] = [
  "PARSING_SKIPPED",
  "PARSING_PENDING",
  "PARSING_FAILED",
  "PARSED",
];

export type LineSheetSetType = "SEASON" | "STOCK" | "API_ORDER";

export const LineSheetSetTypes: LineSheetSetType[] = ["SEASON", "STOCK"];

export interface LineSheetSetPair {
  lineSheetSet: LineSheetSet;
  orderSheetSet: OrderSheetSet | null;
}

export interface LightLineSheetSetPair {
  lineSheetSet: LightLineSheetSet;
  orderSheetSet: LightOrderSheetSet | null;
}

export interface LightLineSheetPair {
  lineSheet: LightLineSheet;
  orderSheets: LightOrderSheet[];
}

export type CommissionType = "PRODUCT" | "ORDER";

export interface LineSheetSet {
  id: number;
  name: string;
  season: string;
  issuedAt: Date;
  issuedByBoutique: Boutique;
  brands: Brand[] | null;
  issuedToBuyers: { id: number; name: string }[];
  status: LineSheetSetStatus;
  reviewStatus: ReviewStatus | null;
  compressionStatus: CompressionStatus | null;
  type: LineSheetSetType;
  validUntil: Date;
  currency: string;
  orderRules: string[];
  orderRuleFiles: StoredObject[];
  attachments: StoredObject[];
  lineSheets: LineSheet[];
  createdByCompany: Agency;
  createdAt: Date;
  updatedAt: Date;
  publishedAt: Date | null;
  externalFileLinks: string[];
  compressedImportedFile: StoredObject | null;
  compressedUnoFormFile: StoredObject | null;
  unoFormFile: StoredObject | null;
  commission?: number | null;
  // 수수료 계산 단위: PRODUCT(상품 단위, 기본) / ORDER(총 주문금액)
  commissionType?: CommissionType | null;
  applyIva?: boolean | null;
  moq?: number | null;
  // 진행 중인 오더(바이어 제출·취소제외) 합산값
  orderedAmount?: number;
  orderedQuantity?: number;
  // 진행 중인 오더(오더시트세트) 수 — 부티크는 이 부티크에 제출된 오더만
  orderedOrderCount?: number;
  isRead: boolean;
  isDeleted: boolean;
  searchedSkus: string[];
  // 패스픽 노출(핫딜) 등록 여부. 스탁오더 세트가 이 값이 켜져 있으면 단품오더가 가능하다.
  sendToPathpick?: boolean;
  // 단품오더 마감. null 이면 무기한. (uno-axios 의 날짜 변환 대상 키가 아니라 문자열로 온다)
  pathpickDeadline?: string | null;
  aiImageCompletedAt?: string | null;
  applyConditionCompletedAt?: string | null;
  orderRuleStatus?: OrderRuleStatus;
  // 바이어 오더하기 우선 노출 — 지금 기간 안이면 true(목록 맨 위 + '추천' 배지). 순서는 서버가 정한다.
  isFeatured?: boolean;
  conditionStatistics?: ConditionStatistics | null;
}

export interface ConditionStatistics {
  brands: ConditionStatisticsBrand[];
}

export interface ConditionStatisticsBrand {
  brandId: number;
  conditions: { by: "MARKUP" | "DISCOUNT"; value: number }[];
}

export interface LightLineSheetSet extends Omit<LineSheetSet, "lineSheets"> {
  lineSheets: LightLineSheet[];
}

export type LineSheetType = "LINE_SHEET" | "ORDER_FORM";

export type ReviewStatus = "SKIPPED" | "PENDING" | "APPROVED" | "REJECTED";
export const ReviewStatuses: ReviewStatus[] = [
  "SKIPPED",
  "PENDING",
  "APPROVED",
  "REJECTED",
];

export type CompressionStatus = "PENDING" | "COMPLETED" | "FAILED";

export interface LineSheet {
  id: number;
  lineSheetSet?: {
    id: number;
  };
  type: LineSheetType;
  name: string;
  file: StoredObject;
  unoFormFile: StoredObject | null;
  status: LineSheetStatus;
  reviewStatus: ReviewStatus | null;
  compressionStatus: CompressionStatus | null;
  lineSheetTabs: LineSheetTab[];
  updatedAt: Date;
  seasons: string[];
  brands: BrandsType[];
  isAllProductsHaveBrand: boolean | null;
  isRead?: boolean;
  searchedSkus?: string[];
  currency?: string | null;
}

export interface LightLineSheet extends Omit<LineSheet, "lineSheetTabs"> {
  lineSheetTabs: LightLineSheetTab[];
}

export interface LineSheetTab {
  index: number;
  name: string;
  products: LineSheetProduct[];
  pdfFile?: StoredObject | null;
  imageFile?: StoredObject | null;
}

export interface LightLineSheetTab extends Omit<LineSheetTab, "products"> {}

export interface DenseLineSheetSet extends Omit<LineSheetSet, "lineSheets"> {
  orderSheetSet?: LightOrderSheetSet;
  lineSheets: DenseLineSheet[];
}

export interface DenseLineSheet extends Omit<LineSheet, "lineSheetTabs"> {
  orderSheets: LightOrderSheet[];
}

export function isLineSheetType(value: string): value is LineSheetSetType {
  const types: LineSheetSetType[] = ["SEASON", "STOCK"];
  return types.some((t) => t === value);
}
